/**
 * ProductTourInfoCard Component
 * 
 * Tour bilgileri kartı (reusable)
 * ProductYachtFeatures benzeri grid yapısında gösterir
 */

'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import { cardVariants } from '@/lib/animations';
import { ProductTourFormatterService } from '@/services/ProductTourFormatterService';
import { TourDuration } from '@/services/ProductTourDataService';
import { useLanguageVariables } from '@/contexts/LanguageVariablesContext';

export interface ProductTourInfoCardProps {
    tourDuration: TourDuration | null;
    season: string | null;
    transfer: string[];
    tourType: string | null;
    durationHours: string | null;
    startLocation: string | null;
    endLocation: string | null;
    /** Ara duraklar (station_city) */
    intermediateStops?: string[];
    distance: number | null;
    minGroupSize: number | null;
    maxGroupSize: number | null;
    /** Grup büyüklüğü metin (meta group_size); yoksa min/max ile formatlanır */
    groupSize?: string | null;
    /** Minimum yaş (örn. "6+", "8-12") – meta min_ages */
    minAges?: string | null;
    /** Rehber dili (meta guide_language; örn. "Guided in English") */
    guideLanguage?: string | null;
    language?: string;
    fields?: Record<string, any>;
    productId?: string | number | null;
}

interface TourFeature {
    key: string;
    label: string;
    value: string | number | null;
}

const DESTINATION_COLLAPSE_CHARS = 160;
const LONG_FEATURE_CHARS = 56;

function DestinationFeatureCell({
    label,
    value,
    destinationLgColSpan,
    cardBg,
}: {
    label: string;
    value: string;
    destinationLgColSpan: string;
    cardBg: string;
}) {
    const { getVariable } = useLanguageVariables();
    const [expanded, setExpanded] = useState(false);
    const needsToggle = value.length > DESTINATION_COLLAPSE_CHARS;
    const showMore = getVariable('tour_detail.show_more', 'Show more');
    const showLess = getVariable('tour_detail.show_less', 'Show less');

    return (
        <div
            className={`min-w-0 col-span-2 md:col-span-3 ${destinationLgColSpan} rounded-lg border p-3 sm:p-4 ${cardBg}`}
        >
            <div className="mb-0.5 text-xs text-alsero-light-gray sm:mb-1 sm:text-sm">{label}</div>
            <div
                className={`text-sm font-semibold break-words text-alsero-dark-gray sm:text-base md:text-lg ${
                    needsToggle && !expanded ? 'line-clamp-4' : ''
                }`}
            >
                {value}
            </div>
            {needsToggle ? (
                <button
                    type="button"
                    className="mt-1.5 text-sm font-medium text-alsero-first underline decoration-alsero-first/40 underline-offset-2 hover:text-alsero-first/90"
                    onClick={() => setExpanded((e) => !e)}
                    aria-expanded={expanded}
                >
                    {expanded ? showLess : showMore}
                </button>
            ) : null}
        </div>
    );
}

export default function ProductTourInfoCard({
    tourDuration,
    season,
    transfer,
    tourType,
    durationHours,
    startLocation,
    endLocation,
    intermediateStops = [],
    distance,
    minGroupSize,
    maxGroupSize,
    groupSize: groupSizeProp = null,
    minAges = null,
    guideLanguage = null,
    language = 'en',
    fields = {},
    productId,
}: ProductTourInfoCardProps) {
    const { getVariable } = useLanguageVariables();
    const formattedDuration = ProductTourFormatterService.formatTourDuration(tourDuration, language);
    const formattedGroupSize = ProductTourFormatterService.formatGroupSize(minGroupSize, maxGroupSize, language);
    const displayGroupSize = (groupSizeProp != null && String(groupSizeProp).trim() !== '') ? String(groupSizeProp).trim() : formattedGroupSize;
    const formattedSeason = ProductTourFormatterService.formatSeason(season, language);
    const formattedTransfer = ProductTourFormatterService.formatTransfer(transfer, language);
    const formattedTourType = ProductTourFormatterService.formatTourType(tourType, language);

    // Field başlıklarını al (çeviri yoksa field title kullan)
    const getFieldTitle = (key: string): string => {
        return fields[key]?.field_title || key;
    };

    // Sıra: duration, tour_type, season, transport, group_size, guide, min_age, start, end, destination (en altta, uzun)
    const features: TourFeature[] = [];

    if (formattedDuration) {
        features.push({
            key: 'tour_duration',
            label: getVariable('tour_detail.duration', getFieldTitle('tour_duration') || 'Duration'),
            value: formattedDuration,
        });
    }

    if (formattedTourType) {
        features.push({
            key: 'tour_type',
            label: getVariable('tour_detail.tour_type', getFieldTitle('tour_type') || 'Tour Type'),
            value: formattedTourType,
        });
    }

    if (formattedSeason) {
        features.push({
            key: 'season',
            label: getVariable('tour_detail.season', getFieldTitle('season') || 'Season'),
            value: formattedSeason,
        });
    }

    if (formattedTransfer) {
        features.push({
            key: 'transfer',
            label: getVariable('tour_detail.transport', getFieldTitle('transfer') || 'Transport'),
            value: formattedTransfer,
        });
    }

    if (displayGroupSize) {
        features.push({
            key: 'group_size',
            label: getVariable('tour_detail.group_size', getFieldTitle('group_size') || getFieldTitle('min_group_size') || 'Group Size'),
            value: displayGroupSize,
        });
    }

    if (guideLanguage) {
        features.push({
            key: 'guide_language',
            label: getVariable('tour_detail.guide', getFieldTitle('guide_language') || 'Guide'),
            value: guideLanguage,
        });
    }

    if (minAges) {
        features.push({
            key: 'min_ages',
            label: getVariable('tour_detail.min_child_age', getFieldTitle('min_ages') || 'Min Child Age'),
            value: minAges,
        });
    }

    if (startLocation) {
        const displayStart = ProductTourFormatterService.simplifyLocationDisplay(startLocation) || startLocation;
        features.push({
            key: 'start_location',
            label: getVariable('tour_detail.start', getFieldTitle('start_location') || 'Start'),
            value: displayStart,
        });
    }

    if (endLocation) {
        const displayEnd = ProductTourFormatterService.simplifyLocationDisplay(endLocation) || endLocation;
        features.push({
            key: 'end_location',
            label: getVariable('tour_detail.end', getFieldTitle('end_location') || 'End'),
            value: displayEnd,
        });
    }

    if (intermediateStops.length > 0) {
        features.push({
            key: 'destinations',
            label: getVariable('tour_detail.destination', 'Destination'),
            value: intermediateStops.join(', '),
        });
    }

    if (features.length === 0 && !productId) return null;

    // Destination her zaman en sonda; diğerleri 5 sütunluk eşit grid, Destination kalan alanı doldurur
    const featuresWithoutDest = features.filter((f) => f.key !== 'destinations');
    const destinationFeature = features.find((f) => f.key === 'destinations');
    const countBeforeDest = featuresWithoutDest.length;
    const remainder = countBeforeDest % 5;

    const cardBg = 'bg-gray-100 border-neutral-200/80';

    // lg: 5 sütun — destination satır genişliği; küçük ekranda grid 2–3 sütun: tam satır (col-span-2 / md:col-span-3), lg’de eski col-span mantığı
    const destinationLgColSpan =
        remainder === 0 || remainder === 4
            ? 'lg:col-span-5'
            : remainder === 1
              ? 'lg:col-span-4'
              : remainder === 2
                ? 'lg:col-span-3'
                : 'lg:col-span-2';

    return (
        <motion.div
            className="mb-4 grid min-w-0 grid-cols-2 gap-3 sm:mb-8 sm:gap-4 md:grid-cols-3 md:gap-5 lg:grid-cols-5 lg:gap-6"
            variants={cardVariants}
        >
            {featuresWithoutDest.map((feature, index) => {
                const str = String(feature.value);
                const spanFull = str.length > LONG_FEATURE_CHARS ? 'col-span-2 md:col-span-1' : '';
                return (
                    <div
                        key={feature.key || index}
                        className={`min-w-0 rounded-lg border p-3 sm:p-4 ${cardBg} ${spanFull}`}
                    >
                        <div className="mb-0.5 text-xs text-alsero-light-gray sm:mb-1 sm:text-sm">{feature.label}</div>
                        <div className="break-words text-sm font-semibold text-alsero-dark-gray sm:text-base md:text-lg">
                            {str}
                        </div>
                    </div>
                );
            })}
            {destinationFeature && (
                <DestinationFeatureCell
                    key={destinationFeature.key}
                    label={destinationFeature.label}
                    value={String(destinationFeature.value)}
                    destinationLgColSpan={destinationLgColSpan}
                    cardBg={cardBg}
                />
            )}
        </motion.div>
    );
}

